import {useCallback, useEffect, useMemo, useState} from "react";
import {assignJobWorker, getAllJobCount} from "../../service/JobService";
import {
    buildStatusCounts,
    buildSummaryCountMap,
    buildWorkerCounts,
    enrichJobsWithSummaryCounts,
    jobMatchesSearch,
    sortSchedulerJobs,
} from "./schedulerUtils";

function readStoredValue(primaryKey, fallbackKey, defaultValue) {
    if (!primaryKey && !fallbackKey) {
        return defaultValue;
    }

    const storedValue = localStorage.getItem(primaryKey);
    if (storedValue) {
        return storedValue;
    }

    if (fallbackKey) {
        const fallbackValue = localStorage.getItem(fallbackKey);
        if (fallbackValue) {
            return fallbackValue;
        }
    }

    return defaultValue;
}

async function loadSummaryCountMap(jobs) {
    const jobIds = jobs.map((job) => job.cos_job_id).filter(Boolean);
    if (jobIds.length === 0) {
        return {};
    }

    const countResponse = await getAllJobCount(jobIds);
    if (countResponse.status !== 200) {
        console.log(await countResponse.text());
        return {};
    }

    const countResult = await countResponse.json();
    return buildSummaryCountMap(countResult);
}

export default function useSchedulerBoardData({
    boardConfig,
    selectedDate,
    searchText,
    fieldWorkerMap,
    fieldSupervisorMap,
}) {
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedStatus, setSelectedStatus] = useState(() => readStoredValue(
        boardConfig.statusStorageKey,
        boardConfig.legacyStatusStorageKey,
        boardConfig.defaultStatus,
    ));
    const [activeWorkerId, setActiveWorkerId] = useState(() => readStoredValue(
        boardConfig.workerStorageKey,
        null,
        boardConfig.defaultWorkerId,
    ));

    const requestDate = boardConfig.usesSelectedDate ? selectedDate : undefined;
    const refreshDependency = boardConfig.usesSelectedDate ? selectedDate : boardConfig.mode;

    const refreshBoard = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage("");

        try {
            const response = await boardConfig.fetchJobs(requestDate);
            if (response.status !== 200) {
                const responseText = await response.text();
                setJobs([]);
                setErrorMessage(responseText || "Unable to load scheduler data.");
                return;
            }

            const jobResult = await response.json();
            const scopedJobs = boardConfig.filterJobs ? boardConfig.filterJobs(jobResult) : jobResult;
            const summaryCountMap = await loadSummaryCountMap(scopedJobs);
            setJobs(enrichJobsWithSummaryCounts(scopedJobs, summaryCountMap));
        } catch (error) {
            console.log("error", error);
            setJobs([]);
            setErrorMessage("Unable to load scheduler data.");
        } finally {
            setIsLoading(false);
        }
    }, [boardConfig, requestDate]);

    useEffect(() => {
        refreshBoard();
    }, [refreshBoard, refreshDependency]);

    useEffect(() => {
        if (boardConfig.statusStorageKey) {
            localStorage.setItem(boardConfig.statusStorageKey, selectedStatus);
        }
    }, [boardConfig.statusStorageKey, selectedStatus]);

    useEffect(() => {
        if (boardConfig.workerStorageKey) {
            localStorage.setItem(boardConfig.workerStorageKey, activeWorkerId);
        }
    }, [activeWorkerId, boardConfig.workerStorageKey]);

    const visibleBacklogJobs = useMemo(() => {
        const filteredJobs = jobs.filter((job) => {
            const matchesStatus = selectedStatus === "ALL" || job.status === selectedStatus;
            return matchesStatus && jobMatchesSearch(job, searchText, fieldWorkerMap, fieldSupervisorMap);
        });
        return sortSchedulerJobs(filteredJobs);
    }, [fieldSupervisorMap, fieldWorkerMap, jobs, searchText, selectedStatus]);

    const workerVisibleJobs = useMemo(() => {
        if (!boardConfig.filterWorkerJobs) {
            return jobs;
        }

        return jobs.filter((job) => boardConfig.filterWorkerJobs(job, {selectedDate}));
    }, [boardConfig, jobs, selectedDate]);

    const workerJobs = useMemo(() => {
        const filteredJobs = workerVisibleJobs.filter((job) => {
            return String(job.cos_job_worker) === String(activeWorkerId) && job.status !== "Cancelled";
        });
        return sortSchedulerJobs(filteredJobs);
    }, [activeWorkerId, workerVisibleJobs]);

    const statusCounts = useMemo(() => buildStatusCounts(jobs), [jobs]);
    const workerCounts = useMemo(() => buildWorkerCounts(workerVisibleJobs), [workerVisibleJobs]);

    const updateJobInState = useCallback((updatedJob) => {
        setJobs((previousJobs) => previousJobs.map((job) => {
            if (job.cos_job_id !== updatedJob.cos_job_id) {
                return job;
            }

            return {
                ...job,
                ...updatedJob,
            };
        }));
    }, []);

    const assignWorker = useCallback(async (jobId, workerId) => {
        setIsLoading(true);
        try {
            const currentJob = jobs.find((job) => job.cos_job_id === jobId);
            const response = currentJob && boardConfig.assignWorker
                ? await boardConfig.assignWorker(currentJob, workerId, {selectedDate})
                : await assignJobWorker(jobId, workerId);
            if (response.status !== 200) {
                console.log(await response.text());
            }
            await refreshBoard();
        } catch (error) {
            console.log("error", error);
            setErrorMessage("Unable to assign worker.");
        } finally {
            setIsLoading(false);
        }
    }, [boardConfig, jobs, refreshBoard, selectedDate]);

    return {
        activeWorkerId,
        assignWorker,
        errorMessage,
        isLoading,
        jobs,
        refreshBoard,
        selectedStatus,
        setActiveWorkerId,
        setSelectedStatus,
        statusCounts,
        updateJobInState,
        visibleBacklogJobs,
        workerCounts,
        workerJobs,
    };
}
